import { ChangeDetectorRef, Component, HostListener, Inject, ViewEncapsulation } from '@angular/core';
import { AppFacade } from '@app/app.facade';
import { map } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

import { SearchBarMobileService } from '@search-bar/services/search-bar-mobile.service';
import { PackagesSearchBarBaseContainer } from '@packages/packages-search-bar/containers/packages-search-bar-base.container';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { SearchType } from '@search-bar/entities/search-type';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { Room } from '@shared/base-search-bar/entities/room.entities';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { PackageStep } from '@packages/packages-search-bar/constants/package-step';
import { GuestToPassengerUIPipe } from '@app/pipes/guest-to-paseenger.pipe';
import { LocalStorageService } from '@app/services/local-storage.service';
import { HeaderService } from '@app/modules/core/header/services/header.service';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { AvoService } from '@avo/services/avo.service';
import { ButtonSize } from '@design-system/components/molecules/button/entities/button.types';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { DateCalendarField } from '@design-system/components/organisms/date-calendar/entities/date-calendar.types';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';

@Component({
  selector: 'app-packages-search-bar-desktop',
  templateUrl: './packages-search-bar-desktop.container.html',
  styleUrls: ['./packages-search-bar-desktop.container.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: false
})
export class PackagesSearchBarDesktopContainer extends PackagesSearchBarBaseContainer {

  public showFlightOptionSelection: boolean = false;
  public activeOption: string;
  public showCalendarInput: boolean = false;
  public activeCalendarType: CalendarField = CalendarField.departure;
  public staticShowClassControls: boolean = false;
  public searchBy = SearchBy.PACKAGES;

  public dateCalendarField: DateCalendarField = 'left';

  public readonly searchIcon = Icon.Search;
  
  @HostListener('document:click', ['$event']) clickout(event: Event): void {
    if (this.isBrowser) {
      this.eventPath.next(event.composedPath());
      if (event['path']) {
        const inputButton = event['path'].find(paths =>
          !!paths['classList'] && paths['classList'].length > 0
          && paths['classList'].contains('travel-calendar-cont'));
        if ((!inputButton && this.showFlightOptionSelection) ||
          (inputButton && this.showCalendarInput && this.showFlightOptionSelection)) {
          this.togglePackagesOption();
        }
      }
    }
  }

  constructor(
    private headerService: HeaderService,
    protected domManipulateService: DomManipulateService,
    protected appFacade: AppFacade,
    protected facade: BaseSearchBarFacade,
    protected searchBarMobileService: SearchBarMobileService,
    protected translocoService: TranslocoService,
    protected cdr: ChangeDetectorRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected guestToPassengerUIPipe: GuestToPassengerUIPipe,
    protected localStorageService: LocalStorageService,
    protected triggerAmplitudeService: TriggerAmplitudeService,
    protected propertiesAmplitudeService: PropertiesAmplitudeService,
    protected mobileDeviceService: MobileDeviceService,
    protected avoService: AvoService,
    @Inject(IS_BROWSER) public isBrowser: boolean,
  ) {
    super(
      domManipulateService, appFacade, facade, searchBarMobileService, translocoService, cdr, 
      router, route, guestToPassengerUIPipe, localStorageService, triggerAmplitudeService, 
      propertiesAmplitudeService, mobileDeviceService, avoService
    );
  }

  get activeFlightClass$(): Observable<FlightClass> {
    return this.flightClass$.pipe(map(flightClass => !flightClass ? FlightClass.CHEAPER : flightClass));
  }

  get packageStep$(): Observable<PackageStep> {
    return this.facade.packageStep$;
  }

  get isHotelPackageView$(): Observable<boolean> {
    return this.packageStep$.pipe(
      map(packageStep => (packageStep === PackageStep.HOTEL_RESULTS && this.isResultsPage) ||
      (!!this.showHotelDetail && packageStep === PackageStep.AIR_PACK_HOTEL_DETAIL))
    );
  }

  get isHotelResult$(): Observable<boolean> {
    return this.packageStep$.pipe(map(packageStep => (packageStep === PackageStep.HOTEL_RESULTS && this.isResultsPage)));
  }

  get isFlightsResult$(): Observable<boolean> {
    return this.packageStep$.pipe(map(packageStep => (packageStep === PackageStep.FLIGHT_RESULTS && this.isResultsPage)));
  }

  get viewFlighOptions$(): Observable<boolean> {
    return this.packageStep$.pipe(map(packageStep => packageStep === PackageStep.FLIGHT_RESULTS && this.isResultsPage));
  }

  get viewHotelRooms$(): Observable<boolean> {
    return this.isHotelPackageView$.pipe(map(isHotelPackageView => isHotelPackageView || !this.isResultsPage));
  }

  get hasDeparture$(): Observable<boolean> {
    return this.departurePlace$.pipe(map(departurePlace => !!departurePlace && !!departurePlace.code));
  }

  get hasDestination$(): Observable<boolean> {
    return this.destinationPlace$.pipe(map(destinationPlace => !!destinationPlace && !!destinationPlace.code));
  }

  get activateArrivalCursor$(): Observable<boolean> {
    return combineLatest([this.hasDeparture$, this.hasDestination$]).pipe(map(
      ([hasDeparture, hasDestination]) => hasDeparture && !hasDestination));
  }

  get showYear(): boolean {
    return this.searchType === SearchType.PACKAGES_RESULT; 
  }
  get isHotelDetail(): boolean {
    return this.showHotelDetail && this.searchType === SearchType.HOTELS_DETAIL; 
  }
  get showDesktopSearchOptions(): boolean {
    return !this.showCalendarInput && this.showFlightOptionSelection; 
  }
  get isSpecialInputStyles(): boolean {
    return this.isResultsPage || this.isHotelDetail; 
  }

  get buttonSize(): ButtonSize {
    return this.isHotelDetail || this.isResultsPage ? ButtonSize.SquareSM : ButtonSize.SquareRG;
  }

  public swapLocation(): void {
    this.selectedDestinationPlace = !this.selectedDestinationPlace;
    this.facade.swapPlaces(this.searchType, true);
  }

  public showSearchInputDeparture(): void {
    this.selectedDestinationPlace = false;
  }

  public showSearchInputArrival(): void {
    this.selectedDestinationPlace = true;
  }

  public setCalendarField(type: DateCalendarField): void {
    this.showCalendarInput = true;
    this.dateCalendarField = type;
  }

  public togglePackagesOption(option?: string): void {
    this.showFlightOptionSelection = !this.showFlightOptionSelection;
    if (option) this.activeOption = option;
  }

  public applyCalendar(event: Date[]): void {
    this.selectCalendar(event);
    this.hideCalendarInput();
  }

  public closeRoomSelector(rooms: Room[]): void {
    if (this.isResultsPage) this.togglePackagesOption();
    this.roomsPackageSelection(rooms);
  }

  public back(): void {
    this.headerService.goBack();
  }

  public searchPackages(): void {
    this.searchPackagesTrigger$.subscribe().unsubscribe();
  }

  private hideCalendarInput(): void {
    this.showCalendarInput = false;
  }

  private selectCalendar(calendar: Date[]): void {
    this.facade.selectCalendarDates(calendar);
  }

}
